<template>
  <div>
    <label class="formLabel" :for="`employee-items-${_uid}`"> {{ label }}</label>
    <div
      class="formSearch"
      :class="{ 'has-Error': required && ids.length === 0 }"
      v-if="multiple || (!multiple && employeeSelection.length === 0)"
    >
      <form autocomplete="off" class="formSearch__InputWrapper" @keydown.enter.prevent="onEnter">
        <icon class="formSearch__Icon" name="search"></icon>
        <p class="formSearch__Loader" v-if="loading">{{ $store.getters.getStringTranslation('loading') }}...</p>
        <input
          :id="`employee-items-${_uid}`"
          class="formInput--Search"
          type="text"
          @keyup="search.length > 2 || search.length === 0 ? getEmployeeOptions() : null"
          @click="getEmployeeOptions()"
          @focus="focused = true"
          @blur="focused = false"
          v-model="search"
          :placeholder="$store.getters.getStringTranslation('searchByName')"
        />
      </form>
      <ul class="formSearch__List" :class="{ 'is-Active': focused === true }" v-if="employeeOptions !== undefined">
        <li class="formSearch__ListItem" v-for="employee in employeeOptions" :key="employee.id">
          <span @click="pushEmployee(employee)">{{ getTitle(employee) }}</span>
        </li>
        <li class="formSearch__ListItem" v-if="noResults === true">
          <span>{{ $store.getters.getStringTranslation('noAuthorsFound') }}</span>
        </li>
        <li class="formSearch__ListItem" v-else-if="employeeOptions.length === 0">
          <span>{{ $store.getters.getStringTranslation('loading') }}...</span>
        </li>
      </ul>
    </div>

    <ul
      class="modal__BuilderResources"
      :class="{ 'modal__BuilderResources--Slim': multiple === false }"
      v-if="employeeSelection.length > 0 && loadingSelection === false"
    >
      <li class="modal__BuilderResourcesItem" v-for="(employee, index) in employeeSelection" :key="employee.id">
        <template v-if="multiple">
          <button type="button" class="modal__BuilderResourcesIcon" @click="moveEmployee(index, index - 1)">
            <icon name="arrow-up"></icon>
          </button>
          <button type="button" class="modal__BuilderResourcesIcon" @click="moveEmployee(index, index + 1)">
            <icon name="arrow-down"></icon>
          </button>
        </template>
        <span>{{ getTitle(employee) }}</span>
        <button type="button" class="modal__BuilderResourcesIcon--Delete" @click="removeEmployee(employee)">
          <icon weight="regular" name="times"></icon>
        </button>
      </li>
    </ul>
    <p class="loader--Authors" v-else-if="loadingSelection === true">
      {{ $store.getters.getStringTranslation('loading') }}...
    </p>
  </div>
</template>

<script>
import debounce from 'tiny-debounce'
import HTTP from '@/http-common.js'

export default {
  props: {
    label: {
      type: String,
      default: 'Employees',
    },
    types: {
      type: Array,
      default: function () {
        return []
      },
    },
    lang: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    employees: {},
    apiKeys: {
      type: Array,
      default: function () {
        return []
      },
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  name: 'SearchEmployee',
  data() {
    return {
      search: '',
      ids: [],
      employeeSelection: [],
      employeeOptions: [],
      loading: false,
      loadingSelection: false,
      noResults: false,
      focused: false,
    }
  },
  computed: {
    user() {
      return this.$store.state.accounts.user
    },
  },
  watch: {
    ids() {
      if (this.multiple) {
        this.$emit('input', this.ids)
        this.$emit('change', this.employeeSelection)
      } else {
        this.$emit('input', this.ids[0])
        this.$emit('change', this.employeeSelection[0])
      }
    },
  },
  methods: {
    getTitle(employee) {
      if (employee) return employee.name
      return null
    },

    // Employee actions
    getEmployeeOptions: debounce(function () {
      this.loading = true
      let limit = null
      if (this.search.length === 0) limit = 10

      this.loading = true
      HTTP.post('search/author', {
        api_keys: this.apiKeys,
        search_query: this.search,
        types: this.types,
        org_id: this.user.org_id,
        limit,
      }).then((response) => {
        this.loading = false
        this.employeeOptions = response.data.authors.filter(
          (a) => this.employeeSelection.map((b) => b.id).indexOf(a.id) === -1
        ) // Don't show options that are already selected

        if (this.employeeOptions.length === 0) {
          this.noResults = true
        } else {
          this.noResults = false
        }
      })
    }, 500),

    pushEmployee(employee) {
      this.ids.push(employee.id)
      this.employeeSelection.push(employee)

      const index = this.employeeOptions.findIndex((x) => x.id === employee.id)
      this.employeeOptions.splice(index, 1)
      this.search = ''
    },

    removeEmployee(employee) {
      this.employeeOptions.push(employee)

      const index = this.employeeSelection.findIndex((x) => x.id === employee.id)
      this.ids.splice(index, 1)
      this.employeeSelection.splice(index, 1)
    },

    moveEmployee(indexA, indexB) {
      if (indexB < 0 || indexB >= this.employeeSelection.length) return

      this.employeeSelection.splice(indexB, 0, this.employeeSelection.splice(indexA, 1)[0])
      this.ids.splice(indexB, 0, this.ids.splice(indexA, 1)[0])
    },
  },
  mounted() {
    if (this.employees) {
      if (typeof this.employees === 'object' && !Array.isArray(this.employees)) {
        this.employeeSelection = [this.employees]
        this.ids = [this.employees.id]
      } else if (this.employees && this.employees.length > 0 && typeof this.employees[0] === 'object') {
        this.employeeSelection = this.employees
        this.ids = this.employees.map((employee) => employee.id)
      } else if (this.employees && this.employees.length > 0 && typeof this.employees[0] === 'number') {
        this.ids = this.employees
        this.loadingSelection = true
        this.employeeSelection = new Array(this.employees.length)

        this.employees.forEach((id, index) => {
          HTTP.get(`author/${id}`)
            .then((response) => {
              // Keep frontend order identical to the incoming backend IDs order.
              this.$set(this.employeeSelection, index, response.data)
            })
            .finally(() => {
              if (this.employeeSelection.every((employee) => employee !== undefined)) {
                this.loadingSelection = false
              }
            })
        })
      }
    }
  },
}
</script>
